import { Injectable } from '@angular/core';

export enum AssessmentModule {
  GML = 'GML',
  LML = 'LML',
  LML_PROFILE = 'LML_PROFILE',
  GOUGES = 'GOUGES',
  DENTS = 'DENTS',
  CRACKS = 'CRACKS',
  PITTING = 'PITTING',
  LAMINATIONS = 'LAMINATIONS',
  BLISTER_HIC = 'BLISTER_HIC',
  DENT_GOUGE = 'DENT_GOUGE'
}

export interface ModuleFeatures {
  supplementalLoadsAvailable: boolean;
  // Add other features as needed in the future
}

/**
 * Service to manage feature availability across different assessment modules.
 * This provides loose coupling between modules by centralizing feature configuration.
 */
@Injectable({
  providedIn: 'root'
})
export class ModuleConfigService {
  private readonly moduleFeatures: Map<AssessmentModule, ModuleFeatures> = new Map([
    [AssessmentModule.GML, { supplementalLoadsAvailable: true }],
    [AssessmentModule.LML, { supplementalLoadsAvailable: false }],
    [AssessmentModule.LML_PROFILE, { supplementalLoadsAvailable: false }],
    [AssessmentModule.GOUGES, { supplementalLoadsAvailable: false }],
    [AssessmentModule.DENTS, { supplementalLoadsAvailable: false }],
    [AssessmentModule.CRACKS, { supplementalLoadsAvailable: false }],
    [AssessmentModule.PITTING, { supplementalLoadsAvailable: false }],
    [AssessmentModule.LAMINATIONS, { supplementalLoadsAvailable: false }],
    [AssessmentModule.BLISTER_HIC, { supplementalLoadsAvailable: false }],
    [AssessmentModule.DENT_GOUGE, { supplementalLoadsAvailable: false }]
  ]);

  private currentModule: AssessmentModule = AssessmentModule.GML;

  constructor() {}

  /**
   * Set the current assessment module being used
   * @param module The assessment module
   */
  setCurrentModule(module: AssessmentModule): void {
    this.currentModule = module;
  }

  /**
   * Get the currently active module
   */
  getCurrentModule(): AssessmentModule {
    return this.currentModule;
  }

  /**
   * Check if a specific feature is available for the current module
   * @param feature The feature to check
   * @returns true if the feature is available, false otherwise
   */
  isFeatureAvailable(feature: keyof ModuleFeatures): boolean {
    const features = this.moduleFeatures.get(this.currentModule);
    return features ? features[feature] : false;
  }

  /**
   * Check if a specific feature is available for a given module
   * @param module The assessment module
   * @param feature The feature to check
   * @returns true if the feature is available for the module, false otherwise
   */
  isFeatureAvailableForModule(module: AssessmentModule, feature: keyof ModuleFeatures): boolean {
    const features = this.moduleFeatures.get(module);
    return features ? features[feature] : false;
  }

  /**
   * Get all features for a specific module
   * @param module The assessment module
   * @returns The features available for the module
   */
  getModuleFeatures(module: AssessmentModule): ModuleFeatures {
    return this.moduleFeatures.get(module) || { supplementalLoadsAvailable: false };
  }

  /**
   * Reset all modules to their default state
   */
  resetAllModules(): void {
    this.currentModule = AssessmentModule.GML;
  }
}
